import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  IndexTable,
  Badge,
  Text,
  Button,
  Banner,
  InlineStack,
  Box,
  Thumbnail,
  Modal,
  TextContainer,
} from '@shopify/polaris';
import { catalogueService, type CatalogueItem } from '../../services/content.service';

function getStatusBadge(status: CatalogueItem['status']) {
  const toneMap: Record<CatalogueItem['status'], 'success' | 'warning' | 'attention'> = {
    generated: 'success',
    pending: 'warning',
    outdated: 'attention',
  };

  const labelMap: Record<CatalogueItem['status'], string> = {
    generated: 'Generated',
    pending: 'Pending',
    outdated: 'Outdated',
  };

  return <Badge tone={toneMap[status]}>{labelMap[status]}</Badge>;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CatalogueManagementPage() {
  const [catalogues, setCatalogues] = useState<CatalogueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Regeneration states
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await catalogueService.list();
      setCatalogues(data);
    } catch (err) {
      setError('Failed to load catalogues');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRegenerate = async (categoryId: string) => {
    try {
      setRegenerating(categoryId);
      setError('');
      const result = await catalogueService.regenerate(categoryId);
      setSuccess(result.message);
      setTimeout(() => setSuccess(''), 5000);
      // Reload data to show updated status
      await loadData();
    } catch (err) {
      setError('Failed to start catalogue regeneration');
      console.error(err);
    } finally {
      setRegenerating(null);
    }
  };

  const handleRegenerateAll = async () => {
    try {
      setRegeneratingAll(true);
      setShowConfirmModal(false);
      setError('');
      const result = await catalogueService.regenerateAll();
      setSuccess(result.message);
      setTimeout(() => setSuccess(''), 5000);
      await loadData();
    } catch (err) {
      setError('Failed to start catalogue regeneration');
      console.error(err);
    } finally {
      setRegeneratingAll(false);
    }
  };

  const outdatedCount = catalogues.filter((c) => c.status === 'outdated').length;
  const pendingCount = catalogues.filter((c) => c.status === 'pending').length;

  const rowMarkup = catalogues.map((catalogue, index) => (
    <IndexTable.Row
      id={catalogue.id}
      key={catalogue.id}
      position={index}
    >
      <IndexTable.Cell>
        <InlineStack gap="300" blockAlign="center">
          {catalogue.bannerUrl && (
            <Thumbnail
              source={catalogue.bannerUrl}
              alt={catalogue.name}
              size="small"
            />
          )}
          <Box>
            <Text variant="bodyMd" fontWeight="bold" as="span">
              {catalogue.name}
            </Text>
            <Text variant="bodySm" tone="subdued" as="p">
              {catalogue.slug}
            </Text>
          </Box>
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {catalogue.productCount} products
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {getStatusBadge(catalogue.status)}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {formatDate(catalogue.catalogueGeneratedAt)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {catalogue.downloadCount}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          {catalogue.catalogueUrl && (
            <Button
              size="slim"
              url={catalogue.catalogueUrl}
              external
            >
              Download
            </Button>
          )}
          <Button
            size="slim"
            loading={regenerating === catalogue.id}
            onClick={() => handleRegenerate(catalogue.id)}
          >
            Regenerate
          </Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Catalogue Management"
      primaryAction={{
        content: 'Regenerate All',
        onAction: () => setShowConfirmModal(true),
        loading: regeneratingAll,
        disabled: catalogues.length === 0,
      }}
    >
      {error && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        </Box>
      )}

      {success && (
        <Box paddingBlockEnd="400">
          <Banner tone="success" onDismiss={() => setSuccess('')}>
            {success}
          </Banner>
        </Box>
      )}

      {/* Summary Cards */}
      <Box paddingBlockEnd="400">
        <InlineStack gap="400">
          <Card>
            <Box padding="400">
              <InlineStack gap="400" blockAlign="center">
                <Box>
                  <Text as="p" variant="headingXl">
                    {catalogues.length}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Total Categories
                  </Text>
                </Box>
              </InlineStack>
            </Box>
          </Card>

          <Card>
            <Box padding="400">
              <InlineStack gap="400" blockAlign="center">
                <Box>
                  <Text as="p" variant="headingXl" tone="caution">
                    {outdatedCount}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Outdated
                  </Text>
                </Box>
              </InlineStack>
            </Box>
          </Card>

          <Card>
            <Box padding="400">
              <InlineStack gap="400" blockAlign="center">
                <Box>
                  <Text as="p" variant="headingXl" tone="caution">
                    {pendingCount}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Pending
                  </Text>
                </Box>
              </InlineStack>
            </Box>
          </Card>

          <Card>
            <Box padding="400">
              <InlineStack gap="400" blockAlign="center">
                <Box>
                  <Text as="p" variant="headingXl">
                    {catalogues.reduce((sum, c) => sum + c.downloadCount, 0)}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Total Downloads
                  </Text>
                </Box>
              </InlineStack>
            </Box>
          </Card>
        </InlineStack>
      </Box>

      <Card padding="0">
        <IndexTable
          resourceName={{ singular: 'catalogue', plural: 'catalogues' }}
          itemCount={catalogues.length}
          headings={[
            { title: 'Category' },
            { title: 'Products' },
            { title: 'Status' },
            { title: 'Last Generated' },
            { title: 'Downloads' },
            { title: 'Actions' },
          ]}
          loading={loading}
          selectable={false}
        >
          {rowMarkup}
        </IndexTable>
      </Card>

      {/* Confirm Regenerate All Modal */}
      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Regenerate All Catalogues"
        primaryAction={{
          content: 'Regenerate All',
          destructive: true,
          onAction: handleRegenerateAll,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowConfirmModal(false),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              Are you sure you want to regenerate all {catalogues.length} catalogues?
              This will queue PDF generation for all categories and may take some time
              to complete.
            </p>
            {outdatedCount > 0 && (
              <p>
                <strong>{outdatedCount} catalogues</strong> are currently outdated and
                will be updated.
              </p>
            )}
          </TextContainer>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
